/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/** Sort direction of a table column */
export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
  NONE = 'NONE',
}

// Used for the format of the PALTableRow
export interface PALTableRow {
  id: string;
  values: Record<string, unknown>;
}

/** Used as the callback type for any function that takes the react-intl formatMessage function */
export type FormatMessageType = (descriptor: string, values?: Record<string, unknown>) => string;

export type ErrorType = {
  message: string;
  ex: string;
  internalEx: string;
  response: {
    message: string;
    ex: string;
    internalEx: string;
  };
};

export type I18NErrorType = {
  i18nMessage: {
    id: string;
    defaultMessage: string;
  };
  values: {
    response: {
      ex: string;
      url: string;
    };
  };
  response: { message: string };
};

export interface FileType extends Blob {
  lastModified: number;
  lastModifiedDate: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}

export type ApiFilter = Record<string, string | number | boolean>;

export type OpenDialogType = (dialogId: string, payload?: any) => void;

export type CloseDialogType = (dialogId: string) => void;

// This is a common type for redux action returns actions
export interface CommonActionType<PayloadType = void> {
  type: string;
  payload?: PayloadType;
}

/** Version number used for V1 and V2 Devicetype/pipeline */
export enum Version {
  V1 = 'V1',
  V2 = 'V2',
}

export type CommonActionsType = {
  addError?: () => void;
  clearError?: () => void;
  getCurrentUser?: () => void;
  getTenantsRelatedToUser?: () => void;
  logoutCurrentUser?: () => void;
  fetchTenantActiveServices?: () => void;
  setTenant?: () => void;
  updateTenant?: () => void;
  clearReduxDataErrors?: () => void;
  fetchServerConfiguration?: () => void;
  openDialog?: OpenDialogType;
  closeDialog?: CloseDialogType;
};

export type IntlType = {
  formats?: object;
  messages?: object;
  defaultLocale?: string;
  defaultFormats?: object;
  fallbackOnEmptyString?: boolean;
  onError?: () => void;
  textComponent?: React.ReactNode;
  locale?: string;
  formatters?: object;
  formatNumber?: () => void;
  formatNumberToParts?: () => void;
  formatRelativeTime?: () => void;
  formatDate?: () => void;
  formatDateToParts?: () => void;
  formatTime?: () => void;
  formatDateTimeRange?: () => void;
  formatTimeToParts?: () => void;
  formatPlural?: () => void;
  formatMessage?: FormatMessageType;
  formatList?: () => void;
  formatListToParts?: () => void;
  formatDisplayName?: () => void;
};

export interface RowActionType {
  id: string;
  labelText?: string;
  renderIcon?: JSX.Element;
  iconDescription?: string;
  isOverflow: boolean;
}

type UserResourcesTypeServices = 'MAXIMO_WORK_ORDER' | 'FACTORY_TALK';

export type UserResourcesType = {
  ORGANIZATION?: string[];
  SITE?: string[];
  SYSTEM?: string[];
  LOCATION_TYPE?: string[];
  LOCATION?: string[];
  ASSET_TYPE?: string[];
  ASSET?: string[];
  DEVICE_TYPE?: string[];
  DEVICE?: Record<string, string[]>;
  CATALOG_FUNCTION?: number[];
  SERVICE?: UserResourcesTypeServices[];
};
